import React, { useEffect, useState } from 'react';
import './DealTeam.scss';
import { useStateSelector } from '../../../../../store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import BootstrapTable from '../../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import {
    ExternalPermissions,
    ExternalPermissionsOptionList,
    ExternalTeamMemberRole,
    ExternalTeamMemberRoleOptionList,
    InternalPermissions,
    InternalPermissionsOptionList,
    InternalTeamMemberRole,
    InternalTeamMemberRoleOptionList,
    TeamMemberType,
    TeamMemberTypeOptionList,
} from '../../MyTeamMembers/Models/Enums';
import createNotification from '../../../../../utils/createNotification';
import { TeamMember } from '../../MyTeamMembers/Models/TeamMember';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import { DialogMode } from '../../../../Associates/Modal/TeamMemberModal';
import { AddOrEditTeamMemberPopup } from './AddOrEditTeamMemberPopup/AddOrEditTeamMemberPopup';
import { MarketplaceTeamMemberMapping } from '../../MyTeamMembers/Models/MarketplaceTeamMemberMapping';
import moment from 'moment';
import PortalTooltip from '../../../../../components/PortalTooltip/PortalTooltip';
import { MarketplaceOpportunityHelper } from '../../MarketplaceOpportunityHelper';
import { EngagementBuyer } from '../../../../../slices/marketplace/timeline/models/engagement-buyer.model';

export interface DealTeamProps {
    isReadonly: boolean;
    opportunityId: number;
    buyer: EngagementBuyer;
}

export interface DealTeamTableRow {
    mappingId: number;
    teamMemberId: number;
    name: string;
    role: string;
    dateAdded: Date;
    permissions: string;
    internalPermissions: InternalPermissions;
    externalPermissions: ExternalPermissions;
    internalRole: InternalTeamMemberRole;
    externalRole: ExternalTeamMemberRole;
    type: string;
}

export const DealTeam: React.FC<DealTeamProps> = (props) => {
    const [membersWithAccessToOpportunity, setMembersWithAccessToOpportunity] =
        useState<TeamMember[]>([]);
    const [
        membersWithoutAccessToOpportunity,
        setMembersWithoutAccessToOpportunity,
    ] = useState<TeamMember[]>([]);
    const axios = useStateSelector((state) => state.core.axios);
    const [isConfirmationDialogVisible, setIsConfirmationDialogVisible] =
        useState(false);
    const [isAddOrEditPopupVisible, setIsAddOrEditPopupVisible] =
        useState(false);
    const [popupMode, setPopupMode] = useState<DialogMode>();
    const [selectedMapping, setSelectedMapping] =
        useState<MarketplaceTeamMemberMapping>(null);
    const [selectedTeamMember, setSelectedTeamMember] =
        useState<TeamMember>(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchUserData();
    }, [props.opportunityId]);

    const fetchUserData = async () => {
        axios
            .get(`/api/TeamMembers`)
            .then((response) => {
                const allMembers: TeamMember[] = response.data;
                const primaryMember = allMembers.find((member) =>
                    member.associateMemberMarketplaceMappings.some(
                        (x) =>
                            x.internalRole ===
                            InternalTeamMemberRole.PrimaryMember
                    )
                );
                const membersWithOpportunityAccess = allMembers.filter(
                    (member) =>
                        member.associateMemberMarketplaceMappings.some(
                            (x) => x.opportunityId === props.opportunityId
                        )
                );
                const membersWithAccess = primaryMember
                    ? [primaryMember, ...membersWithOpportunityAccess]
                    : membersWithOpportunityAccess;
                const membersWithoutAccess = allMembers.filter(
                    (member) =>
                        !(
                            member.associateMemberMarketplaceMappings.some(
                                (x) => x.opportunityId === props.opportunityId
                            ) ||
                            member.associateMemberMarketplaceMappings.some(
                                (x) =>
                                    x.internalRole ===
                                    InternalTeamMemberRole.PrimaryMember
                            )
                        )
                );
                setMembersWithAccessToOpportunity(membersWithAccess);
                setMembersWithoutAccessToOpportunity(membersWithoutAccess);
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            });
    };

    const removeFromOpportunity = () => {
        const teamMemberId = selectedTeamMember?.id;
        const mappingId = selectedMapping?.id;
        axios
            .delete(
                `/api/TeamMembers/${teamMemberId}/RemoveMapping/${mappingId}`
            )
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                fetchUserData();
            });
    };

    const getCurrentOpportunitiesAccessTableData = (): DealTeamTableRow[] => {
        const result = membersWithAccessToOpportunity
            .filter((x) =>
                x.associateMemberMarketplaceMappings.some(
                    (y) =>
                        y.opportunityId === props.opportunityId ||
                        y.internalRole === InternalTeamMemberRole.PrimaryMember
                )
            )
            .map((member) => {
                const memberToOppMapping =
                    member.associateMemberMarketplaceMappings.find(
                        (x) =>
                            x.opportunityId === props.opportunityId ||
                            x.internalRole ===
                                InternalTeamMemberRole.PrimaryMember
                    );
                const permissionsString =
                    memberToOppMapping.type === TeamMemberType.Internal
                        ? InternalPermissionsOptionList.find(
                              (z) =>
                                  z.value ===
                                  memberToOppMapping.internalPermissions
                          )?.label
                        : ExternalPermissionsOptionList.find(
                              (z) =>
                                  z.value ===
                                  memberToOppMapping.externalPermissions
                          )?.label;

                const roleString =
                    memberToOppMapping.type === TeamMemberType.Internal
                        ? InternalTeamMemberRoleOptionList.find(
                              (z) => z.value === memberToOppMapping.internalRole
                          )?.label
                        : ExternalTeamMemberRoleOptionList.find(
                              (z) => z.value === memberToOppMapping.externalRole
                          )?.label;

                const typeString = TeamMemberTypeOptionList.find(
                    (z) => z.value === memberToOppMapping.type
                )?.label;
                const dateAdded =
                    memberToOppMapping.internalRole ===
                    InternalTeamMemberRole.PrimaryMember
                        ? MarketplaceOpportunityHelper.BuyerEngageDate(
                              props.buyer
                          )
                        : memberToOppMapping.dateCreated;

                return {
                    mappingId: memberToOppMapping.id,
                    teamMemberId: member.id,
                    name: member.name,
                    role: roleString,
                    dateAdded: dateAdded,
                    permissions: permissionsString,
                    internalPermissions: memberToOppMapping.internalPermissions,
                    externalPermissions: memberToOppMapping.externalPermissions,
                    internalRole: memberToOppMapping.internalRole,
                    externalRole: memberToOppMapping.externalRole,
                    type: typeString,
                } as DealTeamTableRow;
            });

        return result;
    };

    const getTableColumns = () => [
        { dataField: 'name', text: 'Name', sort: false },
        { dataField: 'type', text: 'Resource Type', sort: false },

        { dataField: 'role', text: 'Role', sort: false },
        {
            dataField: 'dateAdded',
            text: 'Date Added',
            sort: false,
            formatter: (cellContent: Date) =>
                cellContent ? moment(cellContent).format('L') : 'NA',
        },
        {
            dataField: 'permissions',
            text: 'Permissions',
            sort: false,
        },
        {
            dataField: 'mappingId',
            hidden: props.isReadonly,
            text: 'Actions',
            isDummyField: true,
            formatter: (_: number, row: DealTeamTableRow) => {
                return row.internalRole ===
                    InternalTeamMemberRole.PrimaryMember ? (
                    <></>
                ) : (
                    <div className="action-icons">
                        <FontAwesomeIcon
                            onClick={() => {
                                setPopupMode(DialogMode.Edit);
                                setSelectedMapping(
                                    getMappingById(row.mappingId)
                                );
                                setSelectedTeamMember(
                                    getTeamMemberById(row.teamMemberId)
                                );
                                setIsAddOrEditPopupVisible(true);
                            }}
                            icon={faEdit}
                            style={{ cursor: 'pointer' }}
                        />

                        <FontAwesomeIcon
                            className="delete-button"
                            onClick={() => {
                                setSelectedMapping(
                                    getMappingById(row.mappingId)
                                );
                                setSelectedTeamMember(
                                    getTeamMemberById(row.teamMemberId)
                                );
                                setIsConfirmationDialogVisible(true);
                            }}
                            icon={faTrashCan}
                            style={{ cursor: 'pointer', marginLeft: 8 }}
                        />
                    </div>
                );
            },
            headerAlign: 'center',
            align: 'center',
        },
    ];

    const getMappingById = (
        mappingId: number
    ): MarketplaceTeamMemberMapping => {
        const member = membersWithAccessToOpportunity.find((m) =>
            m.associateMemberMarketplaceMappings.some(
                (map) => map.id === mappingId
            )
        );

        return member?.associateMemberMarketplaceMappings.find(
            (map) => map.id === mappingId
        );
    };

    const getTeamMemberById = (teamMemberId: number): TeamMember => {
        return membersWithAccessToOpportunity.find(
            (m) => m.id === teamMemberId
        );
    };

    const navigateToTeamMembersPage = () => {
        navigate('/opportunities?tab=my-team-members');
    };

    const renderHeaderSection = () => {
        return (
            <div className="header-section">
                <div className="title">Team Members</div>
                <div className="buttons-section">
                    <button
                        className="btn btn-primary"
                        id={'assign-team-member-to-opportunity-tooltip-id'}
                        onClick={() => {
                            setPopupMode(DialogMode.Create);
                            setIsAddOrEditPopupVisible(true);
                        }}>
                        Assign to this Opportunity
                    </button>
                    <PortalTooltip
                        anchorId={
                            'assign-team-member-to-opportunity-tooltip-id'
                        }
                        delayShow={0}
                        place="top"
                        content={
                            'Assign a Team Member registered on the platform to this opportunity.'
                        }
                    />
                    <button
                        className="btn btn-primary"
                        id={'add-team-member-to-platform-tooltip-id'}
                        onClick={() => navigateToTeamMembersPage()}>
                        Add to Platform
                    </button>
                    <PortalTooltip
                        anchorId={'add-team-member-to-platform-tooltip-id'}
                        delayShow={0}
                        place="top"
                        content={
                            'Invite a new team member to register on the platform.'
                        }
                    />
                </div>{' '}
            </div>
        );
    };

    return (
        <div className="deal-team">
            {!props.isReadonly && renderHeaderSection()}
            <div className="table-responsive">
                <BootstrapTable
                    bootstrap4
                    key={props.opportunityId}
                    keyField="id"
                    data={getCurrentOpportunitiesAccessTableData()}
                    columns={getTableColumns()}
                    noDataIndication={'No Data available'}></BootstrapTable>
            </div>{' '}
            {isConfirmationDialogVisible && (
                <ConfirmationModal
                    header={`Confirmation`}
                    isVisible={isConfirmationDialogVisible}
                    setIsVisible={setIsConfirmationDialogVisible}
                    confirmationText={`Are you sure you want to remove this team member from this opportunity?`}
                    params={undefined}
                    onConfirm={() => {
                        removeFromOpportunity();
                    }}
                    nextButtonText="Yes, I'm sure"
                    cancelButtonText="No, take me back"></ConfirmationModal>
            )}
            {isAddOrEditPopupVisible && (
                <AddOrEditTeamMemberPopup
                    isVisible={isAddOrEditPopupVisible}
                    membersWithoutAccessToOpportunity={
                        membersWithoutAccessToOpportunity
                    }
                    onClose={() => {
                        fetchUserData();
                        setIsAddOrEditPopupVisible(false);
                    }}
                    mode={popupMode}
                    mapping={selectedMapping}
                    opportunityId={props.opportunityId}
                    teamMember={selectedTeamMember}
                />
            )}
        </div>
    );
};
